import { Box, IconButton, Paper, styled } from '@mui/material'

export interface IAccentProps {
  accentColor: string
}

export interface ICardProps extends IAccentProps {
  isSelected: boolean
  isDragging?: boolean
  isDragOver?: boolean
}

export interface ITopBarPreviewCardProps {
  isSelected: boolean
  accentColor: string
  bgColor: string
}

export const CanvasContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  overflow: 'hidden',
  borderRadius: '0px !important',
}))

export const CanvasHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
}))

export const CanvasContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  backgroundColor: theme.palette.background.default,
  position: 'relative',
}))

export const ContentPlaceholder = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  pointerEvents: 'none',
  margin: 'auto',
}))

export const CanvasItemList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

export const BottomDropZone = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  flex: 1,
  minHeight: 80,
  borderRadius: theme.shape.borderRadius,
  border: `2px dashed ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isActive ? `${theme.palette.primary.main}12` : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  marginTop: theme.spacing(1),
  color: isActive ? theme.palette.primary.main : theme.palette.text.disabled,
  fontSize: '0.75rem',
  userSelect: 'none',
}))

export const CanvasItemCard = styled(Box, {
  shouldForwardProp: prop => !['isSelected', 'accentColor', 'isDragging', 'isDragOver'].includes(prop as string),
})<ICardProps>(({ theme, isSelected, accentColor, isDragging, isDragOver }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${isSelected ? accentColor : theme.palette.divider}`,
  borderTop: isDragOver ? `2px solid ${accentColor}` : undefined,
  backgroundColor: isSelected ? `${accentColor}11` : theme.palette.background.paper,
  cursor: 'grab',
  opacity: isDragging ? 0.4 : 1,
  transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
  '&:hover': {
    borderColor: accentColor,
    backgroundColor: `${accentColor}11`,
  },
  '&:active': {
    cursor: 'grabbing',
  },
}))

export const CanvasItemIcon = styled(Box, {
  shouldForwardProp: prop => prop !== 'accentColor',
})<IAccentProps>(({ accentColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 4,
  backgroundColor: `${accentColor}22`,
  color: accentColor,
  flexShrink: 0,
}))

export const DragHandle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.disabled,
  flexShrink: 0,
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
}))

export const DeleteButton = styled(IconButton)(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: `${theme.palette.error.main}11`,
  },
}))

export const TopBarPreviewCard = styled(Box, {
  shouldForwardProp: prop => !['isSelected', 'accentColor', 'bgColor'].includes(prop as string),
})<ITopBarPreviewCardProps>(({ isSelected, accentColor, bgColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 6,
  backgroundColor: bgColor,
  border: isSelected ? `2px solid ${accentColor}` : '2px solid transparent',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
  boxShadow: isSelected ? `0 0 0 2px ${accentColor}44` : '0 1px 4px rgba(0,0,0,0.2)',
  '&:hover': {
    borderColor: accentColor,
    boxShadow: `0 0 0 2px ${accentColor}44`,
  },
}))

export const TopBarLockedBadge = styled(Box)({
  flexShrink: 0,
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'rgba(255,255,255,0.5)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 3,
  padding: '1px 6px',
  userSelect: 'none',
})

export const CanvasTablePreviewCard = styled(Box, {
  shouldForwardProp: prop => !['isSelected', 'accentColor', 'isDragging', 'isDragOver'].includes(prop as string),
})<ICardProps>(({ theme, isSelected, accentColor, isDragging, isDragOver }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${isSelected ? accentColor : theme.palette.divider}`,
  borderTop: isDragOver ? `2px solid ${accentColor}` : undefined,
  backgroundColor: isSelected ? `${accentColor}11` : theme.palette.background.paper,
  cursor: 'grab',
  opacity: isDragging ? 0.35 : 1,
  transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
  '&:hover': {
    borderColor: accentColor,
    backgroundColor: `${accentColor}11`,
  },
  '&:active': {
    cursor: 'grabbing',
  },
}))

export const TablePreviewBorder = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  overflow: 'hidden',
}))

export const TablePreviewTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '14px',
  fontFamily: 'Arial, sans-serif',
  tableLayout: 'fixed',
  minWidth: 'max-content',
})

export const TablePreviewTh = styled('th', {
  shouldForwardProp: prop => prop !== 'accentColor',
})<{ accentColor?: string }>(({ theme, accentColor }) => ({
  padding: theme.spacing(0.35, 0.75),
  backgroundColor: accentColor ? `${accentColor}22` : theme.palette.grey[100],
  color: accentColor ?? theme.palette.text.primary,
  fontWeight: 600,
  textAlign: 'left',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:last-child': { borderRight: 'none' },
}))

export const TablePreviewTd = styled('td')(({ theme }) => ({
  padding: theme.spacing(0.25, 0.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  '&:last-child': { borderRight: 'none' },
}))
